import React from 'react';
import { Truck, Plane } from 'lucide-react';

/**
 * Variants:
 *  - 'pill'     : default rounded pill (cream bg)
 *  - 'inline'   : small inline text
 *  - 'banner'   : full-width top banner
 *  - 'oblique'  : skewed diagonal tag with icon (eye-catching, sits inline)
 *  - 'ribbon'   : absolute-positioned corner ribbon (use inside a relative parent)
 */
const FreeShippingBadge = ({ variant = 'pill', className = '' }) => {
  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${className}`}
        style={{ color: '#2D5A3D' }}
      >
        <Truck size={14} />
        Free Shipping Worldwide
      </span>
    );
  }

  if (variant === 'banner') {
    return (
      <div
        className={`w-full flex items-center justify-center gap-2 py-2 px-4 text-[12px] font-semibold tracking-wide ${className}`}
        style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
      >
        <Truck size={14} />
        Free Shipping Worldwide · No Hidden Fees
      </div>
    );
  }

  if (variant === 'oblique') {
    // Skewed diagonal tag — parent box stays straight while inner content
    // counter-skews so text reads naturally.
    return (
      <span
        data-testid="free-shipping-oblique"
        className={`free-shipping-oblique inline-flex items-stretch overflow-hidden ${className}`}
        aria-label="Free Shipping Worldwide"
      >
        <span
          className="flex items-center gap-2 pl-4 pr-7 py-2.5 free-shipping-oblique-body"
          style={{
            background: 'linear-gradient(120deg, #2D5A3D 0%, #3F7A53 100%)',
            color: '#FFFFFF',
            transform: 'skewX(-14deg)',
            boxShadow: '0 6px 14px rgba(45,90,61,0.28)',
          }}
        >
          <span
            className="flex items-center gap-2"
            style={{ transform: 'skewX(14deg)' }}
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: '#D9A441', boxShadow: '0 2px 6px rgba(217,164,65,0.4)' }}
            >
              <Plane size={14} color="#FFFFFF" strokeWidth={2.3} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[9px] uppercase tracking-[0.22em] font-bold opacity-90">
                Worldwide
              </span>
              <span className="text-[14px] font-extrabold tracking-tight">
                Free Shipping
              </span>
            </span>
          </span>
        </span>
        {/* Trailing notch */}
        <span
          className="w-3 self-stretch"
          style={{
            background: 'linear-gradient(120deg, #3F7A53 0%, #3F7A53 100%)',
            transform: 'skewX(-14deg) translateX(-10px)',
            opacity: 0.95,
          }}
        />
      </span>
    );
  }

  if (variant === 'ribbon') {
    // Diagonal corner ribbon — requires parent with position:relative and overflow:hidden
    return (
      <div
        data-testid="free-shipping-ribbon"
        className={`absolute top-0 right-0 pointer-events-none select-none ${className}`}
        style={{ width: '180px', height: '180px', overflow: 'hidden' }}
      >
        <div
          className="absolute flex items-center justify-center gap-1.5 font-extrabold tracking-wide text-[12px]"
          style={{
            background: 'linear-gradient(120deg, #D9A441 0%, #E8C77A 50%, #D9A441 100%)',
            color: '#2D5A3D',
            width: '260px',
            top: '38px',
            right: '-70px',
            padding: '10px 0',
            transform: 'rotate(45deg)',
            boxShadow: '0 6px 14px rgba(0,0,0,0.18)',
            textShadow: '0 1px 0 rgba(255,255,255,0.4)',
          }}
        >
          <Plane size={14} strokeWidth={2.4} />
          FREE SHIPPING
        </div>
      </div>
    );
  }

  // default 'pill'
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold ${className}`}
      style={{ backgroundColor: '#F5EDE0', color: '#2D5A3D', border: '1px solid rgba(45,90,61,0.18)' }}
    >
      <Truck size={13} />
      Free Shipping Worldwide
    </span>
  );
};

export default FreeShippingBadge;
